import React from 'react'

import { nutrimaticLabels } from '@/nutrimatic'
import { BurnField as Field } from '../../../../domain/liquidity'
import type { RemoveLiquidityControllerResult } from '../../../../features/liquidity/useRemoveLiquidityController'
import { useLiquidityPositionMetrics } from '../../../../features/liquidity/useLiquidityPositionMetrics'
import { useAppWeb3 } from '../../../../hooks'
import {
  CompactMetricCell,
  CompactMetricsGrid,
  MetricDetailRow,
  MetricsDetailCard,
} from './MobileLiquidityMetricsLayout'
import { formatSharePercent, useProjectedPoolShareAfterRemove } from './liquidityMetricsUtils'

/** Hero de par bajo el título — tableta desktop remove/import. */
export function MobileRemoveLiquidityRateHero({
  currencyA,
  currencyB,
}: {
  currencyA: RemoveLiquidityControllerResult['currencyA']
  currencyB: RemoveLiquidityControllerResult['currencyB']
}) {
  if (!currencyA || !currencyB) {
    return (
      <p className="nm-mobile-simple__rate-hero nm-mobile-simple__rate-hero--idle">
        {nutrimaticLabels.mobileSimple.rateIdle}
      </p>
    )
  }

  return (
    <p className="nm-mobile-simple__rate-hero">
      {currencyA.symbol} / {currencyB.symbol}
    </p>
  )
}

/** Vista compacta bajo inputs — indicadores clave sin repetir patas de salida. */
export function MobileRemoveLiquidityMetricsPanel({
  controller,
}: {
  controller: RemoveLiquidityControllerResult
}) {
  const { account } = useAppWeb3()
  const { currencyA, currencyB, formattedAmounts, pair, parsedAmounts } = controller
  const position = useLiquidityPositionMetrics(pair ?? null, account)
  const projectedShareAfter = useProjectedPoolShareAfterRemove(
    pair ?? undefined,
    parsedAmounts[Field.LIQUIDITY],
    account,
  )

  const percentValue = formattedAmounts[Field.LIQUIDITY_PERCENT]
  const hasWithdrawAmount = percentValue !== '0' && percentValue !== '<1'

  if (!pair || !currencyA || !currencyB) return null

  if (!account) {
    return (
      <p className="nm-mobile-simple__quote-preview nm-mobile-simple__quote-preview--idle px-4 pt-2">
        {nutrimaticLabels.mobileSimple.liquidityPositionConnectHint}
      </p>
    )
  }

  if (!hasWithdrawAmount) {
    return (
      <CompactMetricsGrid>
        <CompactMetricCell
          label={nutrimaticLabels.mobileSimple.currentPoolShare}
          value={formatSharePercent(position.poolShare, { emptyAsDash: true })}
        />
        <CompactMetricCell label={nutrimaticLabels.mobileSimple.lpBalance} value={position.lpBalance} />
      </CompactMetricsGrid>
    )
  }

  return (
    <CompactMetricsGrid>
      <CompactMetricCell
        label={nutrimaticLabels.mobileSimple.removeAmount}
        value={`${percentValue}%`}
        accent
      />
      <CompactMetricCell
        label={nutrimaticLabels.mobileSimple.projectedPoolShare}
        value={formatSharePercent(projectedShareAfter, { emptyAsDash: true })}
      />
      <CompactMetricCell
        label={nutrimaticLabels.mobileSimple.currentPoolShare}
        value={formatSharePercent(position.poolShare, { emptyAsDash: true })}
      />
      <CompactMetricCell label={nutrimaticLabels.mobileSimple.lpBalance} value={position.lpBalance} />
    </CompactMetricsGrid>
  )
}

/** Detalle completo — solo en pantalla de revisión. */
export function MobileRemoveLiquidityReviewDetails({
  controller,
}: {
  controller: RemoveLiquidityControllerResult
}) {
  const { account } = useAppWeb3()
  const {
    allowedSlippage,
    currencyA,
    currencyB,
    formattedAmounts,
    pair,
    parsedAmounts,
    tokenA,
  } = controller
  const position = useLiquidityPositionMetrics(pair ?? null, account)
  const projectedShareAfter = useProjectedPoolShareAfterRemove(
    pair ?? undefined,
    parsedAmounts[Field.LIQUIDITY],
    account,
  )

  return (
    <div className="space-y-3">
      <MetricsDetailCard title={nutrimaticLabels.mobileSimple.withdrawSummaryTitle}>
        <MetricDetailRow
          label={nutrimaticLabels.mobileSimple.removeAmount}
          value={`${formattedAmounts[Field.LIQUIDITY_PERCENT]}%`}
        />
        <MetricDetailRow
          label={nutrimaticLabels.mobileSimple.withdrawTokenAmount(currencyA?.symbol ?? '')}
          value={formattedAmounts[Field.CURRENCY_A] || '—'}
        />
        <MetricDetailRow
          label={nutrimaticLabels.mobileSimple.withdrawTokenAmount(currencyB?.symbol ?? '')}
          value={formattedAmounts[Field.CURRENCY_B] || '—'}
        />
        <MetricDetailRow
          label={nutrimaticLabels.mobileSimple.projectedPoolShare}
          value={formatSharePercent(projectedShareAfter, { emptyAsDash: true })}
        />
      </MetricsDetailCard>

      {account ? (
        <MetricsDetailCard title={nutrimaticLabels.mobileSimple.currentPositionTitle}>
          <MetricDetailRow label={nutrimaticLabels.mobileSimple.lpBalance} value={position.lpBalance} />
          <MetricDetailRow
            label={nutrimaticLabels.mobileSimple.currentPoolShare}
            value={formatSharePercent(position.poolShare, { emptyAsDash: true })}
          />
          <MetricDetailRow
            label={nutrimaticLabels.mobileSimple.positionTokenAmount(currencyA?.symbol ?? '')}
            value={position.token0Amount}
          />
          <MetricDetailRow
            label={nutrimaticLabels.mobileSimple.positionTokenAmount(currencyB?.symbol ?? '')}
            value={position.token1Amount}
          />
        </MetricsDetailCard>
      ) : null}

      {tokenA && pair && currencyA && currencyB ? (
        <MetricsDetailCard title={nutrimaticLabels.mobileSimple.poolRatesTitle}>
          <MetricDetailRow
            label={nutrimaticLabels.mobileSimple.exchangeRate}
            value={`1 ${currencyA.symbol} = ${pair.priceOf(tokenA).toSignificant(6)} ${currencyB.symbol}`}
          />
          <MetricDetailRow label="Slippage" value={`${allowedSlippage / 100}%`} />
        </MetricsDetailCard>
      ) : null}
    </div>
  )
}
